import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import { Search, Users, Phone, Mail, MapPin, Eye, Plus } from 'lucide-react';

const CustomerManagement = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });

  useEffect(() => {
    if (!dataFetched) {
      setDataFetched(true);
      fetchCustomers();
    }
  }, [dataFetched]);

  useEffect(() => {
    filterCustomers();
  }, [customers, searchTerm]);

  const fetchCustomers = async (retryCount = 0) => {
    try {
      const response = await axios.get('/admin/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers:', error);

      // Handle 429 (Too Many Requests) with retry logic
      if (error.response?.status === 429 && retryCount < 1) {
        console.log(`Rate limited, retrying in 1 second...`);
        setTimeout(() => {
          fetchCustomers(retryCount + 1);
        }, 1000);
        return;
      }

      // Use fallback sample data when API fails
      console.log('Using fallback sample data for customers');
      const sampleCustomers = [
        {
          id: 1,
          name: 'Rohit Yeravelli',
          phone: '+91 9876543210',
          email: 'rohit@example.com',
          address: 'Sample Address 1',
          gst_number: 'GST123456789'
        },
        {
          id: 2,
          name: 'Sample Customer',
          phone: '+91 9876543211',
          email: 'sample@example.com',
          address: 'Sample Address 2',
          gst_number: ''
        }
      ];

      setCustomers(sampleCustomers);

      if (error.response?.status === 429) {
        toast.error('Server is busy. Using sample data. Please try again later.');
      } else {
        toast('Using sample data - API connection failed', { icon: '⚠️' });
      }
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    let filtered = customers;

    if (searchTerm) {
      filtered = filtered.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCustomers(filtered);
  };

  const handleViewOrders = (customerId) => {
    // Navigate to customer orders page
    navigate(`/admin/customers/${customerId}/orders`);
  };

  const handleCreateEstimate = (customer) => {
    // Navigate to estimate form with pre-filled customer data
    navigate('/sales/estimate', {
      state: {
        prefilledCustomer: {
          name: customer.name,
          phone: customer.phone,
          email: customer.email,
          address: customer.address,
          gstNumber: customer.gst_number || ''
        }
      }
    });
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post('/admin/customers', formData);
      toast.success('Customer added successfully');
      fetchCustomers();
      setShowAddModal(false);
      setFormData({ name: '', phone: '', email: '', address: '' });
    } catch (error) {
      toast.error('Failed to add customer');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="max-w-7xl mx-auto px-4 py-8">Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Customers</h1>
            <p className="text-gray-600">Manage customer information from estimates and sales</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-[#C5A57D] hover:bg-[#B8985E] text-white px-4 py-2 rounded-lg flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Customer</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="card mb-6">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Customers
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
              placeholder="Search by name, phone, or email..."
            />
          </div>
        </div>
      </div>

      {/* Customers Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCustomers.map((customer) => (
          <div key={customer.id} className="card hover:shadow-lg transition-shadow">
            <div className="flex items-start space-x-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-[#0A5F38]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-slate-900 truncate">
                  {customer.name}
                </h3>
                <div className="mt-2 space-y-2">
                  {customer.phone && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{customer.phone}</span>
                    </div>
                  )}
                  {customer.email && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{customer.email}</span>
                    </div>
                  )}
                  {customer.address && (
                    <div className="flex items-start text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="break-words">{customer.address}</span>
                    </div>
                  )}
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  Customer since: {new Date(customer.created_at).toLocaleDateString()}
                </div>
                <div className="mt-4 flex space-x-2">
                  <button
                    onClick={() => handleViewOrders(customer.id)}
                    className="flex items-center px-3 py-1 bg-blue-100 text-[#0A5F38] rounded-md hover:bg-blue-200 transition-colors text-sm"
                    title="View Orders"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View Orders
                  </button>
                  <button
                    onClick={() => handleCreateEstimate(customer)}
                    className="flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors text-sm"
                    title="Create New Estimate"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    New Estimate
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredCustomers.length === 0 && !loading && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">
            {searchTerm ? 'No customers found matching your search.' : 'No customers found.'}
          </p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="btn-secondary mt-4"
            >
              Clear Search
            </button>
          )}
        </div>
      )}

      {/* Summary */}
      {customers.length > 0 && (
        <div className="mt-8 card">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Customer Summary</h3>
          <div className="grid md:grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-[#0A5F38]">{customers.length}</div>
              <div className="text-sm text-gray-600">Total Customers</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {customers.filter(c => c.email).length}
              </div>
              <div className="text-sm text-gray-600">With Email</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">
                {customers.filter(c => c.address).length}
              </div>
              <div className="text-sm text-gray-600">With Address</div>
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-[#F8F5E6] rounded-2xl max-w-md w-full shadow-2xl border border-[#C5A57D]/20">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-[#0A5F38]">Add New Customer</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-[#C5A57D] hover:text-[#B8985E]"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleAddCustomer} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                    required
                    placeholder="Enter customer name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                    required
                    placeholder="Enter phone number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                    placeholder="Enter email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                    rows="3"
                    placeholder="Enter address"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 border border-[#C5A57D]/30 text-slate-700 rounded-lg hover:bg-[#F3EFE0] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-[#0A5F38] text-white rounded-lg hover:bg-[#084d2e] disabled:opacity-50 transition-colors"
                  >
                    {loading ? 'Adding...' : 'Add Customer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;



